import "./fullscreen-configuration.scss";
import {
  CloseOutlined,
  EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import classnames from "classnames";
import React, { useEffect, useMemo } from "react";
import { useState, FC } from "react";
import FSAddNewForm from "./add-new/add-new-form";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import FCAddNewFormSchema from "./add-new/add-new-form.validation";
import { isEmpty } from "lodash";
import { FullscreenTypeEnums } from "Data/settings";

const initialInput = {
  whitelist: [""],
  daysToExpire: "365",
  type: FullscreenTypeEnums.RACK,
  isNew: true,
  isEdit: true,
  servicePointID: "",
};

const useFCFormControl = () => {
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setError({});
    setInput((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({});
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewWhitelistRow = () => {
    setInput((prev) => ({ ...prev, whitelist: [...prev.whitelist, ""] }));
  };

  const handleDeleteWhitelistRow = (index: number) => {
    // check if the length is 1, do not allow if it is
    if (input.whitelist.length < 2) return;

    // if index is less than 0 by any chance, return
    if (index < 0) return;

    // if the index is more than the length, return
    const length = input.whitelist.length;
    if (index >= length) return;

    setInput((prev) => {
      // remove the value and concatenate
      const left = input.whitelist.slice(0, index);
      const right = input.whitelist.slice(index + 1, length);

      return {
        ...prev,
        whitelist: [...left, ...right],
      };
    });
  };

  const handleWhitelistChange = (value: string, index: number) => {
    // if index is less than 0 by any chance, return
    if (index < 0) return;

    let inputCopy = { ...input };
    inputCopy.whitelist[index] = value;

    setInput(inputCopy);
  };

  return {
    input,
    setInput,
    error,
    setError,
    handleChange,
    handleSelectChange,
    handleAddNewWhitelistRow,
    handleDeleteWhitelistRow,
    handleWhitelistChange,
  };
};

const FCFormControl: FC<any> = (props) => {
  const {
    urlList,
    isSidePanelOpen,
    setIsSidePanelOpen,
    onSubmit,
    onDelete,
    parentInput,
    setParentInput,
    options,
    hasSidePanelMessage,
    setSidePanelMessage,
    isSubmitLoading,
    setIsSubmitLoading,
  } = props || {};

  const { t } = useTranslation();

  const {
    input,
    setInput,
    error,
    setError,
    handleChange,
    handleSelectChange,
    handleAddNewWhitelistRow,
    handleDeleteWhitelistRow,
    handleWhitelistChange,
  } = useFCFormControl();

  const isNewConfig = input.isNew;
  const isViewConfig = !input.isNew && !input.isEdit;
  const isEditConfig = !input.isNew && input.isEdit;

  useEffect(() => {
    if (!isEmpty(parentInput) && !parentInput.isEdit) {
      setInput((prev) => ({
        ...prev,
        isNew: false,
        isEdit: false,
        ...parentInput,
        whitelist: parentInput?.whitelist,
      }));
    }

    // when the panel is showing another rack/trolley, and the user tries to add new, parentInput will have isNew
    // parentInput does not have isNew initially
    if (parentInput.isNew) {
      setInput(initialInput);
    }
  }, [parentInput]);

  const handleSubmit = () => {
    try {
      const value = FCAddNewFormSchema.validateSync(input);

      if (!isEmpty(value)) onSubmit(value);
    } catch (err) {
      console.log(err);

      const { path, value } = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  const handleControlClose = () => {
    if (isNewConfig) {
      setIsSidePanelOpen(false);
      return;
    }

    if (isViewConfig) {
      setIsSidePanelOpen(false);

      // reset parent input
      setParentInput({});
      return;
    }

    setInput((prev) => {
      // filter out any element with ""
      const hasWhitelist = prev.whitelist.filter((w) => w !== "");

      return {
        ...prev,
        isEdit: false,
        whitelist: !isEmpty(hasWhitelist) ? hasWhitelist : null,
      };
    });

    setParentInput((prev) => ({ ...prev, isEdit: false }));
  };

  const handleEditExisting = () => {
    // add isEdit to both child and parentInput

    // if input has no whitelist, create new array
    setInput((prev) => ({
      ...prev,
      isEdit: true,
      whitelist: isEmpty(prev.whitelist)
        ? initialInput.whitelist
        : prev.whitelist,
    }));
    setParentInput((prev) => ({ ...prev, isEdit: true }));
  };

  const handleDeleteConfig = () => {
    const text = t("txt.deleteConfigurationQs");

    if (confirm(text)) onDelete();
  };

  const checkIfValid = useMemo(() => {
    // if the configs have the racks, prevent submission
    if (isEmpty(options)) return true;
    if (input.type === FullscreenTypeEnums.TROLLEY) {
      // if it is in editing mode, allow
      if (!isNewConfig) return true;

      // obtain trolley types
      const fTrolleyUrlList = urlList.filter(
        (u) => u.type === FullscreenTypeEnums.TROLLEY,
      );

      // if there is no servicePointID, all is taken
      const hasAllLocations = fTrolleyUrlList.find((f) => !f?.servicePointID);
      if (input.servicePointID === "" && hasAllLocations) {
        return false;
      }

      const trolleyExists = fTrolleyUrlList.find(
        (u) =>
          u.servicePointID === input.servicePointID &&
          input.servicePointID !== parentInput.servicePointID,
      );

      if (trolleyExists) return false;
      return true;
    }

    // obtain rack types
    const filteredUrlList = urlList.filter(
      (u) => u.type === FullscreenTypeEnums.RACK,
    );

    const alreadyExists = filteredUrlList.find(
      (u) => u.rackID === input.rackID && input.rackID !== parentInput.rackID,
    );
    if (alreadyExists) return false;
    return true;
  }, [input]);

  const submitDisabled = isSubmitLoading || !checkIfValid;

  const formProps = {
    input,
    error,
    isReadonly: !input?.isEdit,
    isViewConfig,
    isEditConfig,
    options,
    handleChange,
    handleSelectChange,
    handleAddNewWhitelistRow,
    handleDeleteWhitelistRow,
    handleWhitelistChange,
  };

  return (
    <div className={classnames("fcv-side-panel", { open: isSidePanelOpen })}>
      <div className="fcv-sp-inner">
        <div className="fcv-sp-header">
          <div className="fcv-sph-left">
            <div className="fcv-sph-title">
              {isNewConfig && t("title.newConfiguration")}
              {isViewConfig && t("title.viewConfiguration")}
              {isEditConfig && t("title.editConfiguration")}
            </div>
          </div>
          <div className="fcv-sph-right">
            <div className="fcv-sph-btn-container">
              {isViewConfig && (
                <span
                  className="fcv-sph-btn fcv-sph-edit"
                  onClick={() => handleEditExisting()}
                >
                  <EditOutlined style={{ fontSize: 18 }} />
                </span>
              )}
              <span
                className="fcv-sph-btn fcv-sph-close"
                onClick={() => handleControlClose()}
              >
                <CloseOutlined style={{ fontSize: 18 }} />
              </span>
            </div>
          </div>
        </div>
        <FSAddNewForm {...formProps} />
        {!checkIfValid && input.isEdit && (
          <div className="u-info u-error">
            {t("txt.thisConfigurationExists")}
          </div>
        )}
        {input?.isEdit && (
          <div className="fcv-sp-footer">
            <div className="fcv-spf-btn-container">
              <span
                className={classnames("fcv-custom-btn", {
                  "disabled-no-color": isSubmitLoading,
                })}
                onClick={() => !isSubmitLoading && handleControlClose()}
              >
                {t("action.cancel")}
              </span>
              <div
                className={classnames("fcv-custom-div-btn", {
                  disabled: submitDisabled,
                  primary: !isSubmitLoading,
                })}
                onClick={() => !submitDisabled && handleSubmit()}
              >
                <span>{t("action.submit")}</span>
                {isSubmitLoading && <LoadingOutlined />}
              </div>
            </div>
          </div>
        )}
        {input._id && !input.isEdit && (
          <div className="fcv-sp-footer floating">
            <div className="fcv-spf-btn-container">
              <div
                className={classnames("fcv-custom-div-btn fcv-btn-full", {
                  disabled: isSubmitLoading,
                  destructive: !isSubmitLoading,
                })}
                onClick={() => !isSubmitLoading && handleDeleteConfig()}
              >
                <span>{t("action.deleteConfiguration")}</span>
                {isSubmitLoading && <LoadingOutlined />}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FCFormControl;
